import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { referrer_name: '', referrer_phone: '', friend_name: '', friend_phone: '' };

const Field = ({ id, label, value, onChange, type = 'text' }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-semibold text-gray-700">{label}</Label>
    <Input id={id} type={type} required value={value} onChange={onChange} data-testid={`referral-${id}-input`} />
  </div>
);

export const ReferralModal = ({ open, onClose, language }) => {
  const en = language === 'en';
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/referrals`, form);
      setStatus('done');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    }
  };

  const close = () => { setStatus('idle'); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="referral-modal">
        {status === 'done' ? (
          <div className="text-center py-6" data-testid="referral-success">
            <CheckCircle className="w-14 h-14 text-[#1a5c3b] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">{en ? 'Thank you!' : 'Merci!'}</h3>
            <p className="text-gray-600">{en ? 'We will contact your friend soon. Once they become a client, your $350 is yours.' : 'Nous contacterons votre ami sous peu. Dès qu’il devient client, vos 350 $ sont à vous.'}</p>
            <Button onClick={close} className="mt-6 bg-[#1a5c3b] hover:bg-[#165432]" data-testid="referral-close-btn">{en ? 'Close' : 'Fermer'}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <DialogHeader>
              <DialogTitle className="text-2xl">{en ? 'Refer a friend' : 'Référez un ami'}</DialogTitle>
              <DialogDescription>{en ? 'Earn $350 cash when your friend becomes a client.' : 'Recevez 350 $ comptant quand votre ami devient client.'}</DialogDescription>
            </DialogHeader>
            <p className="text-xs font-bold uppercase tracking-wider text-[#1a5c3b]">{en ? 'Your info' : 'Vos coordonnées'}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field id="referrer-name" label={en ? 'Your name' : 'Votre nom'} value={form.referrer_name} onChange={set('referrer_name')} />
              <Field id="referrer-phone" type="tel" label={en ? 'Your phone' : 'Votre téléphone'} value={form.referrer_phone} onChange={set('referrer_phone')} />
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#1a5c3b]">{en ? 'Your friend' : 'Votre ami'}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field id="friend-name" label={en ? 'Friend’s name' : 'Nom de l’ami'} value={form.friend_name} onChange={set('friend_name')} />
              <Field id="friend-phone" type="tel" label={en ? 'Friend’s phone' : 'Téléphone de l’ami'} value={form.friend_phone} onChange={set('friend_phone')} />
            </div>
            {status === 'error' && <p className="text-sm text-red-600" data-testid="referral-error">{en ? 'Something went wrong, please try again.' : 'Une erreur est survenue, veuillez réessayer.'}</p>}
            <Button type="submit" disabled={status === 'sending'} className="w-full bg-[#1a5c3b] hover:bg-[#165432] py-5 font-bold" data-testid="referral-submit-btn">
              {status === 'sending' ? (en ? 'Sending...' : 'Envoi...') : (en ? 'Send my referral' : 'Envoyer ma référence')}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
